import { useState, useRef, useMemo, useEffect } from 'react';
import { X, Save, Plus, Trash2, ImagePlus } from 'lucide-react';
import { toast } from './Toast.jsx';
import DateField from './DateField.jsx';
import { RichNoteBox, NoteFormatButtons, ImageLightbox } from './ui.jsx';
import {
  getAgencySettings as apiGetAgencySettings,
  updateAgencySettings as apiUpdateAgencySettings,
} from '../api.js';

const rid = () => 'n' + Math.random().toString(36).slice(2, 9);

// ── Peso del envío ──────────────────────────────────────────────────────────
// A license travels to the server as ONE JSON body that carries the uploaded
// file AND every photo pasted into its notes, all inline as base64. Each piece
// is capped on its own (5MB the file, 2MB per photo) but the TOTAL never was,
// and notes accept unlimited photos — so a license could add up to 15MB+ and
// the save just died, with no message and nothing in Sentry (ticket 2026-08-13,
// Wendy Cercio, CR INSURANCE LATINO: "NO ME DEJA GUARDAR MAS LICENCIAS").
//
// 8MB of ENCODED payload is the ceiling: base64 inflates bytes by ~37%, so this
// still clears the 5MB file the form promises on screen plus room for notes,
// while staying well under what an intermediary will carry in one request.
const MAX_TOTAL_BYTES = 8 * 1024 * 1024;
const fmtMB = (bytes) => (bytes / (1024 * 1024)).toFixed(1) + ' MB';
// base64/ASCII → one char per byte, so length is the wire size closely enough
// to warn on. Counts the file plus the serialized notes (photos included).
export function licensePayloadBytes(fileData, serializedNotes) {
  return String(fileData || '').length + String(serializedNotes || '').length;
}

// ── Multiple license notes ──────────────────────────────────────────────────
// Lissette 2026-07-28: quiere VARIAS notas por licencia y poder PEGAR fotos en
// ellas. To avoid a production DB migration, the whole notes list is stored
// INSIDE the existing `notes` TEXT column as a versioned JSON envelope. Legacy
// single-string notes still read as one entry, and a single text-only note is
// re-saved as a plain string so old consumers (RichNote, exports) keep working.
// Pasted photos are kept as base64 data URLs on each note — consistent with how
// this module already stores the license file inline (fileData base64).
const NOTES_MARK = '__dkNotes';

export function parseLicenseNotes(raw) {
  const s = (raw == null ? '' : String(raw)).trim();
  if (!s) return [];
  if (s.startsWith('{')) {
    try {
      const o = JSON.parse(s);
      if (o && o[NOTES_MARK] && Array.isArray(o.items)) {
        return o.items.map((n, i) => ({
          id: n.id || (rid() + i),
          text: typeof n.text === 'string' ? n.text : '',
          images: Array.isArray(n.images) ? n.images.filter((im) => im && im.dataUrl).map((im) => ({ name: im.name || 'imagen', dataUrl: im.dataUrl })) : [],
          at: n.at || null,
        }));
      }
    } catch (_) { /* not our envelope → treat as a legacy single note */ }
  }
  return [{ id: 'legacy', text: s, images: [], at: null }];
}

export function serializeLicenseNotes(items) {
  const clean = (items || [])
    .map((n) => ({ id: n.id, text: (n.text || '').trim(), images: (n.images || []).filter((im) => im && im.dataUrl), at: n.at || null }))
    .filter((n) => n.text || n.images.length);
  if (!clean.length) return '';
  // One plain text note, no photos → keep it as a plain string (readable +
  // fully backward-compatible with everything that reads `notes` as text).
  if (clean.length === 1 && clean[0].images.length === 0) return clean[0].text;
  return JSON.stringify({ [NOTES_MARK]: 1, items: clean });
}

function imageFileToNote(file, lang) {
  return new Promise((resolve) => {
    if (!file || !(file.type || '').startsWith('image')) { resolve(null); return; }
    if (file.size > 2 * 1024 * 1024) { toast.warn(lang === 'es' ? 'Imagen muy grande (máx 2MB)' : 'Image too large (max 2MB)'); resolve(null); return; }
    const r = new FileReader();
    r.onload = () => resolve({ name: file.name || 'imagen', dataUrl: r.result });
    r.onerror = () => resolve(null);
    r.readAsDataURL(file);
  });
}

// One editable note: Bold/Highlight toolbar, paste-or-attach photos, delete.
function LicenseNoteItem({ item, onChange, onRemove, onOpenImage, lang }) {
  const es = lang === 'es';
  const boxRef = useRef(null);
  const imgInputRef = useRef(null);
  const addImages = async (files) => {
    const out = [];
    for (const f of Array.from(files || [])) { const im = await imageFileToNote(f, lang); if (im) out.push(im); }
    if (out.length) onChange({ images: [...(item.images || []), ...out] });
  };
  return (
    <div style={{ border: '1px solid var(--c-border,#1a3550)', borderRadius: 10, padding: '.5rem', background: 'var(--c-surface2,#112538)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.35rem', marginBottom: '.35rem' }}>
        <NoteFormatButtons taRef={boxRef} value={item.text} onChange={(v) => onChange({ text: v })} lang={es ? 'es' : 'en'} compact />
        <input ref={imgInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => { addImages(e.target.files); e.target.value = ''; }} />
        <button type="button" className="dkt-btn xs ghost" onClick={() => imgInputRef.current && imgInputRef.current.click()} title={es ? 'Agregar foto' : 'Add photo'}><ImagePlus size={12} /> {es ? 'Foto' : 'Photo'}</button>
        <button type="button" onClick={onRemove} title={es ? 'Borrar nota' : 'Delete note'} style={{ marginLeft: 'auto', background: 'none', border: '1px solid rgba(239,68,68,.3)', color: 'var(--c-red,#ff3d6b)', cursor: 'pointer', padding: '2px 6px', borderRadius: 6, display: 'inline-flex', alignItems: 'center' }}><Trash2 size={12} /></button>
      </div>
      <RichNoteBox boxRef={boxRef} rows={2} value={item.text} onChange={(v) => onChange({ text: v })} lang={es ? 'es' : 'en'}
        placeholder={es ? 'Escribe una nota… (pega una foto con Ctrl+V)' : 'Write a note… (paste a photo with Ctrl+V)'}
        onPasteFiles={addImages} style={{ width: '100%', boxSizing: 'border-box' }} />
      {(item.images || []).length > 0 && (
        <div style={{ display: 'flex', gap: '.35rem', flexWrap: 'wrap', marginTop: '.4rem' }}>
          {item.images.map((im, i) => (
            <div key={i} style={{ position: 'relative' }}>
              <img src={im.dataUrl} alt={im.name || ''} onClick={() => onOpenImage && onOpenImage(im)} title={es ? 'Ver foto' : 'View photo'} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--c-border,#1a3550)', cursor: 'zoom-in' }} />
              <button type="button" onClick={() => onChange({ images: item.images.filter((_, j) => j !== i) })} title={es ? 'Quitar' : 'Remove'}
                style={{ position: 'absolute', top: -6, right: -6, background: 'var(--c-red,#ff3d6b)', border: 'none', color: '#fff', borderRadius: '50%', width: 16, height: 16, cursor: 'pointer', fontSize: 11, lineHeight: '16px', padding: 0 }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Hoisted to module scope ON PURPOSE. When this lived inside LicensesView it
// was a new function identity on every parent render, so any re-render (the
// app's background polling fires one during the ~1-2s PDF parse) UNMOUNTED
// the form and reset its state — the uploaded file + parsed fields vanished
// even though extraction succeeded. A stable module-scope component keeps
// its state across parent re-renders.

import { masDosAnos, fechasDeCertificado } from "../licenseDates.js";
export { masDosAnos };

export const EMPTY_LIC = { id:"", type:"life_health", licenseNumber:"", state:"MD", issuedDate:"", expiryDate:"", ceCredits:"", ceRequired:"", ceDeadline:"", notes:"", carrier:"", appointmentDate:"", npn:"", fileData:"", fileName:"", fileType:"" };


// La lista vive en un módulo plano para que la usen pruebas y lógica sin React.
import { STATES_US } from '../estadosUsa.js';
export { STATES_US };

/**
 * CATEGORIAS PROPIAS DE LA AGENCIA, ademas de las de fabrica.
 *
 * Lissette 2026-09-02, queja 7: queria crear una categoria suya en Licencias y
 * Certificados y que SE QUEDE guardada. Antes esta lista era fija en el codigo,
 * asi que cualquier papel que no encajara acababa en "Otro" — y "Otro" no se
 * puede filtrar ni contar.
 *
 * Se guardan en AgencySetting.licenseTypes (migracion
 * 2026_09_02_tipos_de_licencia_de_la_agencia.sql, aplicada en produccion antes
 * que este codigo). Mismo patron que los origenes del lead y los estados de
 * poliza.
 *
 * VAN AL FINAL, DESPUES DE LAS DE FABRICA, y eso importa: LicensesView usaba
 * `LICENSE_TYPES[5]` como reserva, o sea una posicion. Meterlas al principio
 * habria cambiado en silencio de que tipo es esa reserva. (Esa reserva tambien
 * se arregla en LicensesView: apuntaba a "Certificacion" cuando deberia ser
 * "Otro".)
 *
 * El servidor ya impide que una categoria propia use el id de una de fabrica;
 * aqui se filtra otra vez porque esta lista tambien se pinta con datos que
 * pueden venir de una version anterior del servidor.
 */
/**
 * El id de una categoria propia sale de su nombre, sin acentos ni espacios:
 * viaja dentro de License.type y se compara con los de fabrica. Lo usan el
 * gestor de categorias y el "+ Nuevo tipo" del formulario.
 */
export function idDeCategoria(nombre) {
  return String(nombre || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
}

export function getLicenseTypes(lang, propias = []) {
  const deFabrica = getLicenseTypesDeFabrica(lang);
  const idsDeFabrica = new Set(deFabrica.map((t) => t.id));
  const es = lang === "es";
  const extra = (Array.isArray(propias) ? propias : [])
    .filter((t) => t && t.id && !idsDeFabrica.has(t.id))
    .map((t) => ({
      id: t.id,
      label: (es ? (t.labelEs || t.label) : (t.label || t.labelEs)) || t.id,
      icon: t.icon || "🪪",
      color: t.color || "#6366f1",
      propia: true,
    }));
  return [...deFabrica, ...extra];
}

/**
 * Las categorias de la agencia, cargadas una vez y compartidas.
 *
 * Mismo ciclo de vida que `useAgencyTaskStatuses` en TasksView: cache a nivel
 * de modulo para no volver a pedirlas en cada montaje, y `persist` que guarda
 * optimista y avisa si el guardado falla — un catalogo que parece guardado y no
 * lo esta es justo la queja que se esta arreglando.
 */
let _cacheTiposDeLicencia = null;
export function useAgencyLicenseTypes() {
  const [propias, setPropias] = useState(_cacheTiposDeLicencia || []);
  useEffect(() => {
    const isApi = typeof window !== 'undefined' && !!window.__deskteAuth;
    if (!isApi) return;
    let vivo = true;
    apiGetAgencySettings()
      .then((s) => {
        if (!vivo) return;
        const lista = Array.isArray(s?.licenseTypes) ? s.licenseTypes : [];
        _cacheTiposDeLicencia = lista;
        setPropias(lista);
      })
      .catch(() => { /* se queda con lo que hubiera; el modulo sigue usable */ });
    return () => { vivo = false; };
  }, []);
  const persist = async (lista) => {
    _cacheTiposDeLicencia = lista;
    setPropias(lista);
    const isApi = typeof window !== 'undefined' && !!window.__deskteAuth;
    if (!isApi) return;
    try {
      await apiUpdateAgencySettings({ licenseTypes: lista });
    } catch (e) {
      toast.error((lang0() === 'es' ? 'No se pudo guardar la categoria: ' : 'Could not save the category: ') + (e.message || e));
    }
  };
  return [propias, persist];
}

/** El idioma del navegador, solo para el aviso de error de arriba. */
function lang0() {
  try { return (document.documentElement.lang || 'es').slice(0, 2); } catch { return 'es'; }
}

function getLicenseTypesDeFabrica(lang) {
  return [
    { id:"life_health", label:lang==="es"?"Vida y Salud":"Life & Health", icon:"🏥", color:"#3b82f6" },
    { id:"property_casualty", label:lang==="es"?"P&C / Propiedad":"P&C / Property", icon:"🏠", color:"#0891b2" },
    { id:"surplus_lines", label:lang==="es"?"Líneas Excedentes":"Surplus Lines", icon:"📋", color:"#8b5cf6" },
    { id:"adjuster", label:lang==="es"?"Ajustador":"Adjuster", icon:"🔍", color:"#f59e0b" },
    { id:"title", label:lang==="es"?"Título":"Title", icon:"📜", color:"#64748b" },
    { id:"certification", label:lang==="es"?"Certificación":"Certification", icon:"🎓", color:"#10b981" },
    // Las HORAS CONTINUAS son su propio papel: el "Compliance Evaluation" del
    // estado no es una licencia ni una certificación, es el conteo de horas de
    // una licencia (Lissette 2026-08-17). Con tipo propio se puede guardar
    // aparte y filtrar por él, en vez de tener que colgarlo de otra cosa.
    { id:"ce", label:lang==="es"?"Horas continuas (CE)":"Continuing education (CE)", icon:"⏱️", color:"#f97316" },
    { id:"other", label:lang==="es"?"Otro":"Other", icon:"🪪", color:"#6366f1" },
  ];
}

function parseLicenseText(text) {
  const result = {};
  // pdf.js often splits a date into separate glyph runs, so the flattened
  // text comes out as "10 / 08 / 2020" or "10- 08 -2020". Collapse spaces
  // around / - . that sit between digits so the date regexes can see them.
  const flat = text.replace(/\s+/g," ")
    .replace(/(\d)\s*([\/\-.])\s*(\d)/g,"$1$2$3")
    .replace(/(\d)\s*([\/\-.])\s*(\d)/g,"$1$2$3");
  // License Number — must contain a digit (lookahead) so it doesn't grab
  // prose like "License number, your name…".
  const licNum = flat.match(/(?:LICENSE\s*NBR|License|Licencia|License\s*(?:#|No\.?|Number))\s*[:#]?\s*((?=[-A-Z0-9]*\d)[A-Z0-9][-A-Z0-9]{3,20})/i)
    || flat.match(/(?:#|No\.?|Number)\s*[:]?\s*((?=[-A-Z0-9]*\d)[A-Z0-9][-A-Z0-9]{4,20})/i)
    || flat.match(/\b([A-Z]\d{5,12})\b/);
  if(licNum) result.licenseNumber = licNum[1].trim();

  // ── HORAS CONTINUAS (educación continua / CE) ─────────────────────────────
  // El "Compliance Evaluation" del Estado de Florida (dice.fldfs.com) es el
  // papel con el que la agencia lleva las horas, y hasta ahora había que
  // teclearlo a mano aunque los tres campos ya existían en la ficha
  // (Lissette 2026-08-17: "si yo subo el pdf créame eso allí").
  //
  //   In Compliance for Period Ending 9/30/2026
  //                        Requeridas  Completadas  Faltan
  //     4 Hours Update          4           4          0
  //     Elective               16          16          0
  //     Total                  20          20          0
  //
  // Se lee la fila TOTAL, que es la que manda: las parciales (Update,
  // Elective) suman ahí y no hacen falta por separado. El ORDEN de las tres
  // columnas viene del encabezado del propio documento —Required, Completed,
  // Needed— y se comprueba que cuadre (requeridas - completadas = faltan)
  // antes de creerse los números: si no cuadran, es que la tabla salió
  // desordenada del PDF y es mejor no rellenar nada que rellenar mal.
  const ceTotal = flat.match(/\bTotal\b[^\d]{0,20}(\d{1,3}(?:\.\d)?)\s+(\d{1,3}(?:\.\d)?)\s+(\d{1,3}(?:\.\d)?)/i);
  if(ceTotal) {
    const req = parseFloat(ceTotal[1]), done = parseFloat(ceTotal[2]), need = parseFloat(ceTotal[3]);
    if(Number.isFinite(req) && Number.isFinite(done) && Number.isFinite(need)
       && req > 0 && req <= 200 && Math.abs((req - done) - need) < 0.51) {
      result.ceRequired = String(req);
      result.ceCredits = String(done);
    }
  }
  // La fecha límite de las horas. "Period Ending 9/30/2026" es la de Florida;
  // las otras dos formas cubren los estados que lo llaman de otra manera.
  const ceEnd = flat.match(/Period\s*Ending\s*[:#]?\s*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}|\d{4}-\d{2}-\d{2})/i)
    || flat.match(/(?:CE|Continuing\s*Education)[^.\n]{0,40}?(?:Due|Deadline|by)\s*[:#]?\s*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}|\d{4}-\d{2}-\d{2})/i)
    || flat.match(/Compliance\s*(?:Period|Date)[^\d]{0,20}(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}|\d{4}-\d{2}-\d{2})/i);
  // NPN. Also handles "License No. (NPN): 18756496" (Kansas et al.) where
  // the license number IS the NPN.
  const npn = flat.match(/(?:NPN|National Producer Number)\s*[:#]?\s*(\d{5,12})/i)
    || flat.match(/License\s*(?:No\.?|Number)\s*\(\s*NPN\s*\)\s*[:#]?\s*(\d{5,12})/i);
  if(npn) result.npn = npn[1];
  const stateMap = {AL:"AL",AK:"AK",AZ:"AZ",AR:"AR",CA:"CA",CO:"CO",CT:"CT",DE:"DE",DC:"DC",FL:"FL",GA:"GA",HI:"HI",ID:"ID",IL:"IL",IN:"IN",IA:"IA",KS:"KS",KY:"KY",LA:"LA",ME:"ME",MD:"MD",MA:"MA",MI:"MI",MN:"MN",MS:"MS",MO:"MO",MT:"MT",NE:"NE",NV:"NV",NH:"NH",NJ:"NJ",NM:"NM",NY:"NY",NC:"NC",ND:"ND",OH:"OH",OK:"OK",OR:"OR",PA:"PA",RI:"RI",SC:"SC",SD:"SD",TN:"TN",TX:"TX",UT:"UT",VT:"VT",VA:"VA",WA:"WA",WV:"WV",WI:"WI",WY:"WY"};
  const stateNames = {alabama:"AL",alaska:"AK",arizona:"AZ",arkansas:"AR",california:"CA",colorado:"CO",connecticut:"CT",delaware:"DE","district of columbia":"DC",florida:"FL",georgia:"GA",hawaii:"HI",idaho:"ID",illinois:"IL",indiana:"IN",iowa:"IA",kansas:"KS",kentucky:"KY",louisiana:"LA",maine:"ME",maryland:"MD",massachusetts:"MA",michigan:"MI",minnesota:"MN",mississippi:"MS",missouri:"MO",montana:"MT",nebraska:"NE",nevada:"NV","new hampshire":"NH","new jersey":"NJ","new mexico":"NM","new york":"NY","north carolina":"NC","north dakota":"ND",ohio:"OH",oklahoma:"OK",oregon:"OR",pennsylvania:"PA","rhode island":"RI","south carolina":"SC","south dakota":"SD",tennessee:"TN",texas:"TX",utah:"UT",vermont:"VT",virginia:"VA",washington:"WA","west virginia":"WV",wisconsin:"WI",wyoming:"WY"};
  const stateCtx = flat.match(/(?:State|Estado|Resident State|State of)\s*[:#]?\s*([A-Za-z ]{2,20})/i);
  if(stateCtx) {
    const s = stateCtx[1].trim();
    if(stateMap[s.toUpperCase()]) result.state = s.toUpperCase();
    else if(stateNames[s.toLowerCase()]) result.state = stateNames[s.toLowerCase()];
  }
  if(!result.state) {
    for(const [name, code] of Object.entries(stateNames)) {
      if(flat.toLowerCase().includes(name)) { result.state = code; break; }
    }
  }
  if(/life.*health|health.*life|vida.*salud|salud.*vida/i.test(flat)) result.type = "life_health";
  else if(/property.*casualty|P\s*&\s*C|casualty/i.test(flat)) result.type = "property_casualty";
  else if(/surplus/i.test(flat)) result.type = "surplus_lines";
  else if(/adjuster|ajustador/i.test(flat)) result.type = "adjuster";
  else if(/title|título/i.test(flat)) result.type = "title";
  // El "Compliance Evaluation" del estado: no es una licencia ni una
  // certificación, es el conteo de HORAS CONTINUAS. Se detecta por su propio
  // encabezado para que el tipo salga solo al subir el PDF.
  if(/compliance\s*evaluation|continuing\s*education|\bCE\s*requirement/i.test(flat)) result.type = "ce";
  // Certifications (carrier / AHIP / NABIP / PY plan-year agent certs) — a
  // different shape from a state license: a title + completion date + NPN.
  // Detect last so it wins, and capture the certification NAME into notes.
  if(result.type !== "ce" && /(?:agent|product|carrier|annual|returning|new)\s+certification|certification\s+training|certificaci[oó]n|\bAHIP\b|\bNABIP\b|PY\s*20\d{2}/i.test(flat)) {
    result.type = "certification";
    const titleM = flat.match(/(PY\s*20\d{2}[A-Za-z0-9 .,'&\/-]{0,70}|[A-Za-z0-9][A-Za-z0-9 .,'&\/-]{0,70}?Certification(?:\s+Training)?)/i);
    if(titleM && !result.notes) result.notes = titleM[1].replace(/\s+/g," ").trim();
  }
  const D = "(\\d{1,2}[\\/\\-]\\d{1,2}[\\/\\-]\\d{2,4}|\\d{4}-\\d{2}-\\d{2}|[A-Za-z]{3,9}\\.?\\s+\\d{1,2},?\\s*\\d{4}|\\d{1,2}\\s+[A-Za-z]{3,9}\\.?\\s*\\d{4})";
  const GAP = "\\s*[:#\\-]?\\s*(?:[^\\d\\n]{0,40}?)";
  const ISSUE_LBL = "(?:Effective(?:\\s*Date)?|Date\\s*(?:Issued|of\\s*Issue|Granted)|Issuance\\s*Date|Issued?\\s*(?:On|Date)?|Issue\\s*Date|Original\\s*Issue(?:\\s*Date)?|Orig\\.?\\s*Issue|Eff\\.?\\s*Date|Valid\\s*From|Granted|Fecha\\s*(?:de\\s*)?(?:Emisi[oó]n|Expedici[oó]n|Inicio))";
  const EXP_LBL = "(?:Expir(?:ation|y|es|e)?(?:\\s*(?:On|Date))?|Exp\\.?\\s*(?:Date)?|Valid\\s*(?:Through|Thru|Until|To)|Good\\s*(?:Through|Until)|Renew(?:al|s|\\s*by)?(?:\\s*(?:On|Date|Due))?|Fecha\\s*(?:de\\s*)?(?:Expiraci[oó]n|Vencimiento|Caducidad))";
  const parseDate = (s) => {
    if(!s) return "";
    s = s.trim();
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if(iso) return s;
    const mdy = s.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if(mdy) { let y = mdy[3]; if(y.length===2) y = (parseInt(y)>50?"19":"20")+y; return `${y}-${mdy[1].padStart(2,"0")}-${mdy[2].padStart(2,"0")}`; }
    const mos = {january:"01",february:"02",march:"03",april:"04",may:"05",june:"06",july:"07",august:"08",september:"09",october:"10",november:"11",december:"12",
      jan:"01",feb:"02",mar:"03",apr:"04",jun:"06",jul:"07",aug:"08",sep:"09",oct:"10",nov:"11",dec:"12"};
    const mdy2 = s.match(/([A-Za-z]{3,9})\.?\s+(\d{1,2}),?\s*(\d{4})/);
    if(mdy2 && mos[mdy2[1].toLowerCase()]) return `${mdy2[3]}-${mos[mdy2[1].toLowerCase()]}-${mdy2[2].padStart(2,"0")}`;
    const dmy = s.match(/(\d{1,2})\s+([A-Za-z]{3,9})\.?\s*(\d{4})/);
    if(dmy && mos[dmy[2].toLowerCase()]) return `${dmy[3]}-${mos[dmy[2].toLowerCase()]}-${dmy[1].padStart(2,"0")}`;
    return "";
  };
  // `parseDate` ya existe aquí, así que la fecha de las horas se normaliza
  // ahora (la busqueda está arriba, junto al resto del bloque de CE).
  if(ceEnd) { const d = parseDate(ceEnd[1]); if(d) result.ceDeadline = d; }
  // Date strategy. Real state-license PDFs scramble text and use row-major
  // tables, so weak label proximity grabs the WRONG date (verified vs
  // AZ/MD/TN/LIC/KS samples 2026-05-19). Most reliable signal across every
  // sample: among the *plausible* license dates, the EARLIEST is the issue
  // date and the LATEST is the expiry — middle dates (seal/print date) are
  // noise. Filter to a sane window first so a DOB ("1985") or a far-future
  // CE date can't skew min/max. Fall back to labelled parsing only when
  // there aren't ≥1 plausible dates.
  const nowY = new Date().getFullYear();
  const allDates = [...new Set((flat.match(new RegExp(D,"gi"))||[]).map(parseDate).filter(Boolean))]
    .filter(d => { const y = +d.slice(0,4); return y >= 2000 && y <= nowY + 15; })
    .sort();
  if(allDates.length >= 2){ result.issuedDate = allDates[0]; result.expiryDate = allDates[allDates.length - 1]; }
  else if(allDates.length === 1){ result.expiryDate = allDates[0]; }
  else {
    const datePatterns = [ new RegExp(ISSUE_LBL + GAP + D, "i") ];
    const expiryPatterns = [ new RegExp(EXP_LBL + GAP + D, "i") ];
    for(const p of datePatterns) { const m=flat.match(p); if(m) { result.issuedDate=parseDate(m[1]); if(result.issuedDate) break; } }
    for(const p of expiryPatterns) { const m=flat.match(p); if(m) { result.expiryDate=parseDate(m[1]); if(result.expiryDate) break; } }
  }
  // UN CERTIFICADO NO SE LEE COMO UNA LICENCIA (Lissette, 2026-09-03: "toma
  // fecha de expiracion como la fecha de completacion; es muy diferente a
  // licencia"). La regla de arriba —la mas temprana es la emision, la mas
  // tardia el vencimiento— esta hecha para el PDF de una licencia estatal, que
  // trae las dos. Un certificado no se emite y vence: se COMPLETA. Aplicandole
  // la regla de la licencia, su fecha de completacion acababa en la casilla de
  // vencimiento cuando era la ultima del documento, o se inventaba un
  // vencimiento a partir de la fecha de impresion.
  //
  // El parche anterior solo rescataba el caso de UNA sola fecha; con dos o mas
  // —que es lo normal en un certificado de AHIP o de aseguradora, que trae la
  // de completacion y la de impresion— seguia fallando.
  if(result.type === "certification") {
    const f = fechasDeCertificado(flat, allDates);
    result.issuedDate = f.issuedDate;
    result.expiryDate = f.expiryDate;
  }
  return result;
}

// F-N4 (audit 2026-05-22): CDN scripts now load with pinned versions +
// SHA-384 integrity hashes via the central cdnVendor registry. A CDN
// compromise can no longer inject JavaScript into our authenticated origin.
import { loadCdnScript, PDFJS_3_11_174, PDFJS_WORKER_3_11_174, TESSERACT_5_1_1 } from "../cdnVendor.js";

async function ensurePdfjs() {
  if (window.pdfjsLib) return;
  await loadCdnScript(PDFJS_3_11_174).catch(() => { throw new Error("pdfjs-load-failed"); });
  window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_3_11_174.url;
}

// Tesseract.js runs ENTIRELY in the browser (WASM). The user's document
// never leaves the device — only the library/lang assets are fetched from
// the CDN. That keeps OCR HIPAA-safe (no BAA needed). Chosen by Lissette
// 2026-05-19 over server-side AI extraction (Anthropic BAA still pending).
async function ensureTesseract() {
  if (window.Tesseract) return;
  await loadCdnScript(TESSERACT_5_1_1).catch(() => { throw new Error("ocr-load-failed"); });
}

async function ocrRecognize(image) {
  await ensureTesseract();
  const { data } = await window.Tesseract.recognize(image, "eng");
  return (data && data.text) || "";
}

// Render up to `maxPages` PDF pages to canvases so OCR can read scanned /
// broken-font PDFs that have no usable text layer.
async function pdfPagesToCanvases(arr, maxPages) {
  await ensurePdfjs();
  const pdf = await window.pdfjsLib.getDocument({ data: arr }).promise;
  const out = [];
  for (let p = 1; p <= Math.min(pdf.numPages, maxPages); p++) {
    const page = await pdf.getPage(p);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
    out.push(canvas);
  }
  return out;
}

function dataUrlToUint8(dataUrl) {
  const bin = atob(dataUrl.split(",")[1]);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return arr;
}

async function pdfDigitalText(arr) {
  await ensurePdfjs();
  const pdf = await window.pdfjsLib.getDocument({ data: arr }).promise;
  let text = "";
  for (let p = 1; p <= Math.min(pdf.numPages, 3); p++) {
    const pg = await pdf.getPage(p);
    const tc = await pg.getTextContent();
    text += tc.items.map(i => i.str).join(" ") + "\n";
  }
  return text;
}

// Unified, format-agnostic extraction:
//  1. PDF with a real text layer → fast pdf.js text.
//  2. If that yields no recognizable fields (scrambled/broken-font PDFs)
//     OR it's an image/photo → in-browser OCR (Tesseract) then re-parse.
// onStage(stage) lets the UI say "reading with OCR, this can take a bit".
async function extractLicense(file, dataUrl, onStage) {
  const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
  if (isPdf) {
    let digital = "";
    try { digital = await pdfDigitalText(dataUrlToUint8(dataUrl)); } catch (e) { if (e.message === "pdfjs-load-failed") throw e; }
    const flat = digital.replace(/\s+/g, " ").trim();
    if (flat.length > 10) {
      const parsed = parseLicenseText(digital);
      if (Object.keys(parsed).length > 0) return { extracted: parsed, rawText: flat, method: "pdf-text" };
    }
    // No usable text → OCR the rendered pages.
    onStage && onStage("ocr");
    const canvases = await pdfPagesToCanvases(dataUrlToUint8(dataUrl), 2);
    let otext = "";
    for (const c of canvases) otext += (await ocrRecognize(c)) + "\n";
    const oflat = otext.replace(/\s+/g, " ").trim();
    return { extracted: oflat.length > 5 ? parseLicenseText(otext) : {}, rawText: oflat, method: "ocr-pdf" };
  }
  // Image / photo → OCR directly.
  onStage && onStage("ocr");
  const otext = await ocrRecognize(dataUrl);
  const oflat = otext.replace(/\s+/g, " ").trim();
  return { extracted: oflat.length > 5 ? parseLicenseText(otext) : {}, rawText: oflat, method: "ocr-image" };
}

// `renovando`: el formulario sale con lo de la version vigente y se guarda como
// RENOVACION: la anterior pasa al historial de la tarjeta (Lissette 2026-10-20).
// `onNuevoTipo(nombre)`: crea una categoria propia desde el campo Tipo y
// devuelve su id (o null si no se pudo).
export function LicenseForm({ initial, onSave, onCancel, lang, defaultNpn, tiposPropios = [], renovando = false, onNuevoTipo }) {
  // New license: pre-fill NPN from the agent's profile (editable). An NPN is
  // the producer's, not the license's — it's the same across all their
  // licenses, so defaulting it saves typing on every one.
  const [f, setF] = useState(initial || { ...EMPTY_LIC, npn: defaultNpn || "" });
  // Notes are edited as a LIST (varias notas con fotos). Seeded from the stored
  // `notes` (JSON envelope or legacy string); serialized back into f.notes on
  // save. Start with one empty note so there's always a box to type in.
  const [noteItems, setNoteItems] = useState(() => {
    const parsed = parseLicenseNotes((initial || {}).notes);
    return parsed.length ? parsed : [{ id: rid(), text: "", images: [], at: null }];
  });
  const noteHasContent = noteItems.some((n) => (n.text || "").trim() || (n.images || []).length);
  const [noteLightbox, setNoteLightbox] = useState(null); // { src, name }
  const addNote = () => setNoteItems((prev) => [{ id: rid(), text: "", images: [], at: new Date().toISOString() }, ...prev]);
  const patchNote = (id, patch) => setNoteItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const removeNote = (id) => setNoteItems((prev) => prev.filter((x) => x.id !== id));
  const [parsing, setParsing] = useState(false);
  const [stage, setStage] = useState(""); // "" | "parse" | "ocr"
  // Weight this record already had when the form opened. The size cap is
  // enforced against the LARGER of it and MAX_TOTAL_BYTES, so a license saved
  // before the cap existed stays editable — its number and dates can still be
  // corrected — it just can't be made any heavier.
  const [initialBytes] = useState(() => licensePayloadBytes((initial || {}).fileData, (initial || {}).notes));
  // Memoized: this JSON.stringify's every note photo, so running it on each
  // render would stutter the form on exactly the records that are heavy enough
  // to matter — i.e. the ones this weight check exists for.
  const serializedNotes = useMemo(() => serializeLicenseNotes(noteItems), [noteItems]);
  const payloadBytes = useMemo(
    () => licensePayloadBytes(f.fileData, serializedNotes),
    [f.fileData, serializedNotes],
  );
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const isEdit = !!initial;
  // "+ Nuevo tipo…" en el campo Tipo: se escribe el nombre ahi mismo y queda
  // guardado como categoria de la agencia para las proximas.
  const [nuevoTipo, setNuevoTipo] = useState(null); // null = cerrado; string = lo que se escribe
  const [creandoTipo, setCreandoTipo] = useState(false);
  const crearTipo = async () => {
    const nombre = String(nuevoTipo || '').trim();
    if (!nombre || !onNuevoTipo) return;
    setCreandoTipo(true);
    try {
      const id = await onNuevoTipo(nombre);
      if (id) { set("type", id); setNuevoTipo(null); }
    } finally { setCreandoTipo(false); }
  };
  const LICENSE_TYPES = getLicenseTypes(lang, tiposPropios);
  // Un certificado no se emite y vence: se completa. La pantalla cambia de
  // nombre las casillas de fecha segun lo que se este guardando, en vez de
  // pedir "fecha de emision" de algo que nunca se emitio (Lissette 2026-09-03).
  const esCertificado = f.type === "certification";

  const handleLicenseFile = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.warn(lang === "es" ? "Archivo muy grande (máx 5MB)" : "File too large (max 5MB)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      const es = lang === "es";
      setF(prev => ({ ...prev, fileData: dataUrl, fileName: file.name, fileType: file.type }));
      const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      const isImg = (file.type || "").startsWith("image") || /\.(png|jpe?g|webp|gif|bmp|tiff?)$/i.test(file.name);
      if (!isPdf && !isImg) return; // stored, but nothing to auto-read
      setParsing(true);
      setStage("parse");
      let outcome = "error", rawText = "", method = "";
      const filled = [];
      let ocrAnnounced = false;
      try {
        const res = await extractLicense(file, dataUrl, (st) => {
          if (st === "ocr" && !ocrAnnounced) {
            ocrAnnounced = true;
            setStage("ocr");
            toast.info(es ? "Leyendo con OCR en tu navegador… puede tardar ~10-20s" : "Reading with in-browser OCR… may take ~10-20s");
          }
        });
        method = res.method || "";
        rawText = res.rawText || "";
        const extracted = res.extracted || {};
        const L = { licenseNumber: es ? "número" : "number", npn: "NPN", state: es ? "estado" : "state", type: es ? "tipo" : "type", issuedDate: es ? "emisión" : "issued", expiryDate: es ? "vencimiento" : "expiry", ce: es ? "horas continuas" : "CE hours", ceDeadline: es ? "fecha límite de horas" : "CE deadline" };
        setF(prev => {
          const u = { ...prev, fileData: dataUrl, fileName: file.name, fileType: file.type };
          if (extracted.licenseNumber && !prev.licenseNumber) u.licenseNumber = extracted.licenseNumber;
          if (extracted.npn && !prev.npn) u.npn = extracted.npn;
          if (extracted.state) u.state = extracted.state;
          if (extracted.type) u.type = extracted.type;
          if (extracted.issuedDate && !prev.issuedDate) u.issuedDate = extracted.issuedDate;
          if (extracted.expiryDate && !prev.expiryDate) u.expiryDate = extracted.expiryDate;
          // Horas continuas: se PISAN a proposito, al reves que el resto. Un
          // Compliance Evaluation nuevo trae el conteo al dia y ese es justo
          // el motivo de subirlo; conservar el viejo seria guardar un numero
          // que ya se sabe vencido.
          if (extracted.ceRequired) u.ceRequired = extracted.ceRequired;
          if (extracted.ceCredits) u.ceCredits = extracted.ceCredits;
          if (extracted.ceDeadline) u.ceDeadline = extracted.ceDeadline;
          return u;
        });
        if (extracted.licenseNumber) filled.push(L.licenseNumber);
        if (extracted.npn) filled.push(L.npn);
        if (extracted.state) filled.push(L.state);
        if (extracted.type) filled.push(L.type);
        if (extracted.issuedDate) filled.push(L.issuedDate);
        if (extracted.expiryDate) filled.push(L.expiryDate);
        if (extracted.ceCredits || extracted.ceRequired) filled.push(`${L.ce} (${extracted.ceCredits||"0"}/${extracted.ceRequired||"?"})`);
        if (extracted.ceDeadline) filled.push(L.ceDeadline);
        outcome = filled.length ? "ok" : (rawText.length > 5 ? "noFields" : "noText");
      } catch (err) {
        outcome = (err && (err.message === "pdfjs-load-failed" || err.message === "ocr-load-failed" || err.message === "script-load-failed")) ? "loadFail" : "error";
        console.warn("License extract error:", err);
      }
      if (outcome === "noFields" && rawText) {
        const snippet = rawText.slice(0, 1500);
        setNoteItems(prev => {
          const has = prev.some(n => (n.text || "").trim() || (n.images || []).length);
          return has ? prev : [{ id: rid(), text: "[Texto leído del documento / document text read]\n" + snippet, images: [], at: new Date().toISOString() }];
        });
        try { navigator.clipboard && navigator.clipboard.writeText(snippet); } catch {}
      }
      setParsing(false);
      setStage("");
      const via = (method === "ocr-image" || method === "ocr-pdf") ? "OCR" : "PDF";
      if (outcome === "ok") toast.success(es ? `Datos extraídos (${via}): ${filled.join(", ")}` : `Extracted (${via}): ${filled.join(", ")}`);
      else if (outcome === "noText") toast.warn(es ? "No pude leer texto del archivo ni con OCR. El archivo se guardó; llena los campos a mano." : "Couldn't read text from the file even with OCR. The file was saved; fill the fields manually.");
      else if (outcome === "noFields") toast.warn(es ? "Leí el documento pero no reconocí los campos. El archivo se guardó; llena lo que falte a mano." : "Read the document but didn't recognize the fields. The file was saved; fill in the rest manually.");
      else if (outcome === "loadFail") toast.warn(es ? "No pude cargar el lector / OCR (revisa tu conexión). El archivo se guardó; llena los campos a mano." : "Couldn't load the reader / OCR (check your connection). The file was saved; fill the fields manually.");
      else toast.warn(es ? "No pude leer el archivo. Se guardó; llena los campos a mano." : "Couldn't read the file. It was saved; fill the fields manually.");
    };
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 14, padding: "1.5rem", maxWidth: 540, width: "100%", maxHeight: "90vh", overflow: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
          <div style={{ fontWeight: 800, fontSize: "1rem", color: "var(--c-white,#eef6ff)" }}>🪪 {renovando ? (lang === "es" ? "Renovar" : "Renew") : isEdit ? (lang === "es" ? "Editar Licencia" : "Edit License") : (lang === "es" ? "Nueva Licencia" : "New License")}</div>
          <button type="button" onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text3,#45688a)" }}><X size={18} /></button>
        </div>
        {renovando && (
          <div style={{ fontSize: ".75rem", color: "var(--c-text2,#7aa3c0)", background: "var(--c-cyan-dim)", border: "1px solid rgba(0,212,255,.3)", borderRadius: 8, padding: ".5rem .7rem", marginBottom: ".75rem", lineHeight: 1.45 }}>
            {lang === "es"
              ? "La versión de ahora pasa al historial de esta misma tarjeta, con su archivo. Sube aquí el documento nuevo y pon las fechas nuevas."
              : "The current version moves to this card's history, with its file. Upload the new document here and set the new dates."}
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".65rem", marginBottom: ".65rem" }}>
          <div>
            <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{lang === "es" ? "Tipo" : "Type"}</label>
            <select className="dkt-inp" value={f.type} onChange={e => { if (e.target.value === "__nuevo__") { setNuevoTipo(""); return; } set("type", e.target.value); }} style={{ width: "100%" }}>
              {LICENSE_TYPES.map(lt => <option key={lt.id} value={lt.id}>{lt.icon} {lt.label}</option>)}
              {onNuevoTipo && <option value="__nuevo__">{lang === "es" ? "➕ Nuevo tipo…" : "➕ New type…"}</option>}
            </select>
            {nuevoTipo !== null && (
              <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                <input className="dkt-inp" autoFocus value={nuevoTipo} onChange={e => setNuevoTipo(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); crearTipo(); } if (e.key === "Escape") setNuevoTipo(null); }}
                  placeholder={lang === "es" ? "Nombre del tipo" : "Type name"} style={{ flex: 1, minWidth: 0 }} />
                <button type="button" className="dkt-btn xs" disabled={creandoTipo || !String(nuevoTipo).trim()} onClick={crearTipo}>{lang === "es" ? "Crear" : "Create"}</button>
                <button type="button" className="dkt-btn xs ghost" onClick={() => setNuevoTipo(null)} title={lang === "es" ? "Cancelar" : "Cancel"}><X size={12} /></button>
              </div>
            )}
          </div>
          <div>
            <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{lang === "es" ? "Estado (US)" : "State"}</label>
            <select className="dkt-inp" value={f.state} onChange={e => set("state", e.target.value)} style={{ width: "100%" }}>
              {STATES_US.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".65rem", marginBottom: ".65rem" }}>
          <div>
            <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{lang === "es" ? "# Licencia" : "License #"}</label>
            <input className="dkt-inp" value={f.licenseNumber} onChange={e => set("licenseNumber", e.target.value)} placeholder="" style={{ width: "100%", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>NPN</label>
            <input className="dkt-inp" value={f.npn || ""} onChange={e => set("npn", e.target.value)} placeholder="" style={{ width: "100%", boxSizing: "border-box" }} />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".65rem", marginBottom: ".65rem" }}>
          <div>
            {/* Un certificado se COMPLETA; una licencia se emite y vence. Son
                dos papeles distintos y la casilla no puede llamarse igual en
                los dos (Lissette, 2026-09-03). */}
            <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>
              {esCertificado
                ? (lang === "es" ? "Fecha de Completación" : "Completion Date")
                : (lang === "es" ? "Fecha Emisión" : "Issued Date")}
            </label>
            <DateField className="dkt-inp" value={f.issuedDate} onChange={e => set("issuedDate", e.target.value)} style={{ width: "100%", boxSizing: "border-box" }} />
          </div>
          <div>
            {/* <div>, no <label>: dentro hay un boton. Al pulsar en cualquier
                parte de un <label> que no sea contenido interactivo, el
                navegador dispara un clic sobre el primer control etiquetable
                que haya dentro — aqui el "+2 años" —, asi que tocar el texto
                "Fecha Expiración" reescribia la fecha sin que nadie la tocara.
                Mismo fallo que el pop up de IA en Secuencias (E-121). */}
            <div style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "flex", alignItems: "center", gap: ".4rem", marginBottom: 4 }}>
              <span>
                {lang === "es" ? "Fecha Expiración" : "Expiry Date"}
                {esCertificado && (
                  <span style={{ fontWeight: 600, textTransform: "none", letterSpacing: 0, color: "var(--c-text3,#45688a)", marginLeft: 5 }}>
                    {lang === "es" ? "(solo si el certificado la trae)" : "(only if the certificate states one)"}
                  </span>
                )}
              </span>
              {/* Sugerencia de Wendy: muchos documentos no traen vencimiento y
                  la renovación normal son dos años. Solo aparece cuando hay
                  fecha de emisión de la que partir — sin ella no hay nada que
                  sumar y un botón muerto solo confunde. */}
              {/* "+2 años" es la renovacion tipica de una LICENCIA. Un
                  certificado no se renueva a dos años, asi que el boton
                  desaparece: sugerir ahi una fecha inventada es justo lo que
                  ensucia las alertas de "por vencer". */}
              {!esCertificado && f.issuedDate && masDosAnos(f.issuedDate) && (
                <button
                  type="button"
                  onClick={() => set("expiryDate", masDosAnos(f.issuedDate))}
                  title={lang === "es"
                    ? "Poner la fecha de emisión + 2 años. Puedes cambiarla después."
                    : "Set the issued date + 2 years. You can change it afterwards."}
                  style={{ background: "none", border: "1px solid var(--c-border,#1a3550)", borderRadius: 6, cursor: "pointer", color: "var(--c-cyan,#00d4ff)", fontSize: ".6rem", fontWeight: 700, padding: "1px 6px", textTransform: "none", letterSpacing: 0 }}
                >
                  +2 {lang === "es" ? "años" : "yrs"}
                </button>
              )}
            </div>
            <DateField className="dkt-inp" value={f.expiryDate} onChange={e => set("expiryDate", e.target.value)} style={{ width: "100%", boxSizing: "border-box" }} />
          </div>
        </div>
        <div style={{ fontSize: ".6rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".4rem", marginTop: ".3rem", fontFamily: "var(--font-mono)" }}>📚 {lang === "es" ? "Educación Continua (CE)" : "Continuing Education (CE)"}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: ".65rem", marginBottom: ".65rem" }}>
          <div>
            <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{lang === "es" ? "Créditos Completados" : "Credits Done"}</label>
            <input type="number" className="dkt-inp" value={f.ceCredits || ""} onChange={e => set("ceCredits", e.target.value)} placeholder="0" style={{ width: "100%", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{lang === "es" ? "Créditos Requeridos" : "Required"}</label>
            <input type="number" className="dkt-inp" value={f.ceRequired || ""} onChange={e => set("ceRequired", e.target.value)} placeholder="24" style={{ width: "100%", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{lang === "es" ? "Fecha Límite CE" : "CE Deadline"}</label>
            <DateField className="dkt-inp" value={f.ceDeadline || ""} onChange={e => set("ceDeadline", e.target.value)} style={{ width: "100%", boxSizing: "border-box" }} />
          </div>
        </div>
        <div style={{ marginBottom: ".65rem" }}>
          <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4 }}>{lang === "es" ? "Carrier / Appointment" : "Carrier / Appointment"}</label>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: ".65rem" }}>
            <input className="dkt-inp" value={f.carrier || ""} onChange={e => set("carrier", e.target.value)} placeholder={lang === "es" ? "Ej: Aetna, CareFirst..." : "E.g.: Aetna, CareFirst..."} style={{ width: "100%", boxSizing: "border-box" }} />
            <DateField className="dkt-inp" value={f.appointmentDate || ""} onChange={e => set("appointmentDate", e.target.value)} style={{ width: "100%", boxSizing: "border-box" }} />
          </div>
        </div>
        <div style={{ marginBottom: ".75rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: 6 }}>
            <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em" }}>{lang === "es" ? "Notas" : "Notes"}</label>
            <button type="button" className="dkt-btn xs ghost" style={{ marginLeft: "auto" }} onClick={addNote}><Plus size={12} /> {lang === "es" ? "Agregar nota" : "Add note"}</button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
            {noteItems.length === 0 && (
              <div style={{ fontSize: ".7rem", color: "var(--c-text3,#45688a)", fontStyle: "italic" }}>{lang === "es" ? 'Sin notas. Toca "Agregar nota" para empezar.' : 'No notes. Tap "Add note" to start.'}</div>
            )}
            {noteItems.map((n) => (
              <LicenseNoteItem key={n.id} item={n} lang={lang}
                onChange={(patch) => patchNote(n.id, patch)}
                onOpenImage={(im) => setNoteLightbox({ src: im.dataUrl, name: im.name })}
                onRemove={() => removeNote(n.id)} />
            ))}
          </div>
        </div>
        {/* FILE UPLOAD */}
        <div style={{ marginBottom: ".75rem" }}>
          <label style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 6 }}>📎 {lang === "es" ? "Archivo de Licencia (imagen o PDF)" : "License File (image or PDF)"}
            {parsing && <span style={{color:"var(--c-cyan,#00d4ff)",marginLeft:8,fontWeight:600,fontSize:".65rem"}}>⏳ {stage==="ocr" ? (lang==="es"?"Leyendo con OCR… (~10-20s)":"Reading with OCR… (~10-20s)") : (lang==="es"?"Extrayendo datos...":"Extracting data...")}</span>}
          </label>
          {f.fileData ? (
            <div style={{ background: "var(--c-surface2,#112538)", border: "1px solid rgba(0,212,255,.25)", borderRadius: 10, padding: ".65rem .8rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: f.fileType?.startsWith("image") ? ".5rem" : 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: ".4rem" }}>
                  <span style={{ fontSize: "1rem" }}>{f.fileType?.includes("pdf") ? "📄" : "🖼️"}</span>
                  <span style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--c-white,#eef6ff)" }}>{f.fileName}</span>
                  <span style={{ fontSize: ".62rem", color: "var(--c-green,#00ff88)", fontWeight: 700 }}>✅ {lang === "es" ? "Cargado" : "Uploaded"}</span>
                </div>
                <button type="button" onClick={() => { set("fileData", ""); set("fileName", ""); set("fileType", ""); }}
                  style={{ background: "none", border: "1px solid rgba(239,68,68,.3)", color: "var(--c-red,#ff3d6b)", cursor: "pointer", padding: "3px 8px", borderRadius: 6, fontSize: ".62rem", fontWeight: 700, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 3 }}>
                  <X size={11} />{lang === "es" ? "Quitar" : "Remove"}
                </button>
              </div>
              {f.fileType?.startsWith("image") && (
                <img src={f.fileData} alt="License" style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8, border: "1px solid var(--c-border,#1a3550)", objectFit: "contain" }} />
              )}
            </div>
          ) : (
            <div onClick={() => document.getElementById("lic-file-input")?.click()}
              style={{ border: "2px dashed var(--c-border,#1a3550)", borderRadius: 10, padding: "1.2rem", textAlign: "center", cursor: "pointer", background: "var(--c-surface2,#112538)", transition: "all .2s" }}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = "rgba(0,212,255,.4)"; e.currentTarget.style.background = "var(--c-cyan-dim)"; }}
              onDragLeave={e => { e.preventDefault(); e.currentTarget.style.borderColor = "var(--c-border,#1a3550)"; e.currentTarget.style.background = "var(--c-surface2,#112538)"; }}
              onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = "var(--c-border,#1a3550)"; e.currentTarget.style.background = "var(--c-surface2,#112538)"; handleLicenseFile(e.dataTransfer?.files?.[0]); }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(0,212,255,.4)"; e.currentTarget.style.background = "var(--c-cyan-dim)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--c-border,#1a3550)"; e.currentTarget.style.background = "var(--c-surface2,#112538)"; }}>
              <div style={{ fontSize: "1.5rem", marginBottom: ".25rem" }}>📎</div>
              <div style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--c-text2,#7aa3c0)" }}>{lang === "es" ? "Click o arrastra el archivo aquí" : "Click or drag the file here"}</div>
              <div style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", marginTop: ".2rem" }}>JPG, PNG, PDF — max 5MB</div>
              <div style={{ fontSize: ".6rem", color: "var(--c-cyan,#00d4ff)", marginTop: ".3rem", fontWeight:600 }}>✨ {lang === "es" ? "PDFs se auto-rellenan los campos" : "PDFs auto-fill fields"}</div>
            </div>
          )}
          <input id="lic-file-input" type="file" accept="image/*,.pdf" style={{ display: "none" }}
            onChange={e => { handleLicenseFile(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
        {(() => {
          // Why the save can't go through, in words the agent can act on. This
          // used to be a bare `disabled` on the button: it greyed out and a
          // click did nothing — no message, no request, nothing in Sentry. From
          // the agent's side that is indistinguishable from a broken CRM, and
          // it is exactly what "no me deja guardar" described. House rule: a
          // save never fails in silence.
          const es = lang === "es";
          const bytes = payloadBytes;
          const cap = Math.max(MAX_TOTAL_BYTES, initialBytes);
          const tooHeavy = bytes > cap;
          // Certifications have no license number / state — they just need a
          // name (notes) or the uploaded PDF. Licenses still require a number.
          const missing = f.type === "certification"
            ? (!(noteHasContent || f.fileName || f.fileData)
              ? (es
                ? "Para guardar una certificación, escribe su nombre en Notas o adjunta el archivo."
                : "To save a certification, type its name in Notes or attach the file.")
              : null)
            : (!f.licenseNumber
              ? (es
                ? "Falta el número de licencia — es el único campo obligatorio."
                : "The license number is missing — it's the only required field.")
              : null);
          const heavyMsg = es
            ? `Este registro pesa ${fmtMB(bytes)} y el máximo es ${fmtMB(cap)}. Quita alguna foto de las notas o sube el archivo más liviano.`
            : `This record weighs ${fmtMB(bytes)} and the limit is ${fmtMB(cap)}. Remove a photo from the notes or upload a lighter file.`;
          const blocker = missing || (tooHeavy ? heavyMsg : null);
          // Show the running weight once it is big enough to matter, so the
          // limit is visible BEFORE it is hit rather than at the moment of
          // failure.
          const showWeight = bytes > cap / 2;
          return <>
            {(blocker || showWeight) && (
              <div style={{
                marginBottom: ".7rem", padding: ".55rem .75rem", borderRadius: 9, fontSize: ".72rem", lineHeight: 1.45,
                background: blocker ? "rgba(255,61,107,.08)" : "rgba(255,183,0,.08)",
                border: `1px solid ${blocker ? "rgba(255,61,107,.3)" : "rgba(255,183,0,.3)"}`,
                color: blocker ? "var(--c-red,#ff3d6b)" : "var(--c-amber,#ffb700)",
              }}>
                {blocker || (es
                  ? `Peso de este registro: ${fmtMB(bytes)} de ${fmtMB(cap)}.`
                  : `This record weighs ${fmtMB(bytes)} of ${fmtMB(cap)}.`)}
              </div>
            )}
            <div style={{ display: "flex", gap: ".5rem", justifyContent: "flex-end" }}>
              <button type="button" onClick={onCancel} className="dkt-btn-s">{es ? "Cancelar" : "Cancel"}</button>
              {/* Deliberately NOT `disabled`: a dead button teaches nothing.
                  Clicking with something missing says what is missing. */}
              <button type="button"
                onClick={() => { if (blocker) { toast.warn(blocker); return; } onSave({ ...f, notes: serializedNotes }); }}
                aria-disabled={!!blocker}
                style={{ display: "flex", alignItems: "center", gap: 5, background: blocker ? "#2a4560" : "linear-gradient(135deg,#0099cc,#0055aa)", border: "none", color: "#fff", fontSize: ".78rem", fontWeight: 700, padding: ".5rem 1.2rem", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>
                <Save size={13} />{es ? "Guardar" : "Save"}
              </button>
            </div>
          </>;
        })()}
      </div>
      <ImageLightbox src={noteLightbox?.src} name={noteLightbox?.name} onClose={() => setNoteLightbox(null)} />
    </div>
  );
}
